// scroll-manager.js
import Lenis from 'lenis';

let lenisInstance;

// Keep track of the requestAnimationFrame ID to cancel it if needed
let rafId;

/**
 * RequestAnimationFrame callback to update Lenis.
 * @param {number} time - The current time.
 */
function raf(time) {
  if (lenisInstance) {
    lenisInstance.raf(time);
  }
  rafId = requestAnimationFrame(raf);
}

/**
 * Initializes the Lenis smooth scrolling instance.
 * @param {object} options - Lenis constructor options.
 * @returns {Lenis|null} The Lenis instance, or null if it couldn't be initialized.
 */
export function initializeScrollManager(options = {}) {
  if (lenisInstance) {
    // console.warn('Scroll manager (Lenis) already initialized.');
    return lenisInstance;
  }

  try {
    lenisInstance = new Lenis(options);
    // console.log('Scroll manager (Lenis) initialized.');
    
    // Start the animation frame loop
    if (rafId) cancelAnimationFrame(rafId); // Clear any existing loop
    rafId = requestAnimationFrame(raf);
  } catch (error) {
    console.error("Failed to initialize Lenis:", error);
    return null;
  }
  
  return lenisInstance;
}

export function getLenisInstance() {
  return lenisInstance;
}

export function destroyScrollManager() {
  if (lenisInstance) {
    lenisInstance.destroy(); // Lenis's internal cleanup
    lenisInstance = null;
  }
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  // console.log('Scroll manager (Lenis) destroyed.');
}

/**
 * Initializes handling for same-page anchor links to scroll smoothly using Lenis.
 * @param {Lenis} lenisInstance - The initialized Lenis instance.
 */
export function initializeLenisAnchorScrolling(lenisInstance) {
  if (!lenisInstance) {
    console.error('Lenis instance not provided for anchor scrolling initialization.');
    return;
  }

  /**
   * Calculates the offset for the header.
   * @returns {number} The negative height of the header, or 0 if not found.
   */
  function getHeaderOffset() {
    const header = document.getElementById('header');
    return header ? -header.offsetHeight : 0;
  }

  document.addEventListener('click', function(event) {
    // If the clicked element or an ancestor has 'data-scroll-ignore',
    // we need to stop both our script and the default link behavior.
    if (event.target.closest('[data-scroll-ignore]')) {
      // If the ignored element is inside ANY link, we must prevent the link's default action
      // (e.g., browser navigation or a Barba.js transition) to allow the inner element
      // (like a slideshow button) to function without triggering the parent link.
      if (event.target.closest('a')) {
        event.preventDefault();
      }
      // In either case, we stop the scroll manager from proceeding.
      return;
    }

    // Find the closest ancestor anchor link with an href starting with '#'
    const link = event.target.closest('a[href*="#"]');

    if (link) {
      const href = link.getAttribute('href');
      let targetToScroll;
      const scrollOptions = {
        lock: true, // Prevent user scroll during programmatic scroll
      };

      // Check if the link is for the current page or another page
      const url = new URL(link.href);
      const isSamePage = url.pathname === window.location.pathname;

      // Handle links like href="#" or href="#top" to scroll to the top of the page
      if (href === '#' || href.toLowerCase() === '#top') {
        targetToScroll = 0;
      } else {
        // For other hashes, if it's a same-page link, check if the target element exists.
        // If it's a link to another page, we let Barba handle it, and the logic
        // in `afterEnter` will scroll to the hash.
        if (!isSamePage) return; // Let Barba handle the page transition

        // The href can be a full URL, a path with a hash, or just a hash.
        // We only want the hash part for the selector.
        const hash = url.hash;
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          scrollOptions.offset = getHeaderOffset();
          targetToScroll = hash; // Use the selector string; Lenis will resolve it.
        } else {
          // If the target element doesn't exist, do not prevent default.
          // This allows the browser to handle it (e.g., if it's a link to another page with a hash,
          // though `href^="#"` primarily targets same-page links) or do nothing if it's a dead anchor.
          console.warn(`Anchor target "${hash}" not found on this page. Default link behavior will proceed.`);
          return;
        }
      }

      // If a valid target for Lenis is determined (0 or an existing selector)
      event.preventDefault(); // Prevent the default anchor jump
      lenisInstance.scrollTo(targetToScroll, scrollOptions);

      // Note: If Lenis is initialized with `syncURL: true` in its constructor options,
      // the URL hash will be updated automatically by Lenis.
    }
  }, false); // Use event delegation on the document

  // Handle scrolling to hash on page load
  function scrollToHash(hash) {
    if (hash && hash !== '#') {
      // Wait a brief moment for the layout to stabilize, especially if images affect header height.
      // This is a common practice, but adjust or remove if not needed for your specific case.
      setTimeout(() => {
        const targetElementOnLoad = document.querySelector(hash);
        if (targetElementOnLoad) {
          const offsetOnLoad = getHeaderOffset();
          lenisInstance.scrollTo(hash, {
            offset: offsetOnLoad,
            // lock: true, // Optional for page load
          });
        }
      }
      , 100); // Small delay, adjust as needed
    }
  }
  scrollToHash(window.location.hash);
}